import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useContent } from "../lib/useContent";
import { mediaSrc } from "../lib/media";

export default function Portfolio() {
    const { categories } = useContent();
    return (
        <div data-testid="portfolio-page" className="pt-32 md:pt-40">
            {/* Intro */}
            <section className="px-6 md:px-12 lg:px-24">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
                    <div className="md:col-span-3">
                        <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                            03 — Portfolio
                        </p>
                    </div>
                    <div className="md:col-span-9">
                        <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tighter text-aplum">
                            Selected works,
                            <br />
                            <em className="italic">in series</em>.
                        </h1>
                        <p className="font-sans-display text-base md:text-lg text-aplum-dim mt-10 max-w-2xl leading-relaxed">
                            The catalogue below traces six concurrent bodies of work — each a sustained
                            conversation with a particular medium, scale, and form of attention. Some
                            series remain open; others are sealed by the year they close.
                        </p>
                    </div>
                </div>
            </section>

            {/* Categories grid */}
            <section className="px-6 md:px-12 lg:px-24 mt-24 md:mt-32">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                    {categories.map((cat, i) => (
                        <motion.div
                            key={cat.slug}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.8, delay: (i % 2) * 0.15 }}
                            className={i % 3 === 0 ? "md:mt-0" : "md:mt-16"}
                        >
                            <Link
                                to={`/portfolio/${cat.slug}`}
                                data-testid={`category-card-${cat.slug}`}
                                className="category-card block group"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={mediaSrc(cat.cover)}
                                        alt={cat.cover?.alt ?? ''}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-aplum/20 group-hover:bg-aplum/50 transition-colors duration-700" />
                                    <div className="absolute inset-0 flex items-center justify-center p-8">
                                        <div className="text-center category-overlay">
                                            <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-dim mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-700 delay-100">
                                                {cat.placeholder ? "In Preparation" : `0${i + 1}`}
                                            </p>
                                            <h2 className="font-serif-display text-3xl md:text-5xl tracking-tighter text-aplum">
                                                {cat.title}
                                            </h2>
                                        </div>
                                    </div>
                                    {cat.placeholder && (
                                        <div className="absolute top-4 right-4 font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent border border-aplum-accent px-3 py-1">
                                            Soon
                                        </div>
                                    )}
                                </div>
                                <div className="mt-5 flex items-baseline justify-between">
                                    <p className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim">
                                        {(cat.subcategories?.length ?? 0) === 0
                                            ? "—"
                                            : (cat.subcategories?.length ?? 0) === 1
                                              ? cat.subcategories[0].label
                                              : `${cat.subcategories?.length ?? 0} Sub-series`}
                                    </p>
                                    <span className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum group-hover:text-aplum-accent transition-colors">
                                        Enter →
                                    </span>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>
        </div>
    );
}
